import React from "react";
import { motion } from "framer-motion";
import { Plane, Home, BadgeCheck, ArrowDown, Sparkles, Languages, Star, TrendingUp } from "lucide-react";
import { useLang } from "@/context/LangContext";

const stat = (n, l) => (
  <div>
    <div className="text-3xl md:text-4xl font-head font-extrabold text-[#FFD166]">{n}</div>
    <div className="text-xs md:text-sm text-slate-300 mt-1">{l}</div>
  </div>
);

export const Hero = ({ onApply }) => {
  const { t } = useLang();
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden pt-28 pb-16 md:pt-40 md:pb-28 ej-grain">
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          alt="Almacén moderno en Europa"
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B132B] via-[#0B132B]/95 to-[#0B132B]/70" />
      </div>

      <div className="orb w-[420px] h-[420px] bg-[#FFD166] -top-24 -right-24 opacity-20" />
      <div className="orb w-[360px] h-[360px] bg-[#E63946] bottom-0 left-1/4 opacity-10" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 bg-[#E63946]/20 text-[#FF8A8A] border border-[#E63946]/40 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF8A8A] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E63946]" />
              </span>
              🇲🇽 {t("hero.badge")} 🇵🇱
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
              className="font-head font-extrabold text-4xl sm:text-5xl lg:text-[4.2rem] leading-[1.03] tracking-tight text-white"
            >
              {t("hero.t1")}
              <span className="text-[#FFD166]">{t("hero.hl")}</span>{t("hero.t2")}
              <span className="bg-white text-[#0B132B] px-2 rounded-md">SHEIN</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
              className="text-slate-300 text-base md:text-lg mt-6 max-w-xl leading-relaxed"
            >
              {t("hero.subtitle")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
              className="flex flex-wrap gap-4 mt-8"
            >
              <button
                onClick={onApply}
                data-testid="hero-apply-btn"
                className="group bg-[#FFD166] text-[#0B132B] font-bold px-8 py-4 rounded-full hover:bg-[#E0A926] hover:shadow-[0_12px_32px_-8px_rgba(255,209,102,0.6)] transition-all flex items-center gap-2"
              >
                {t("hero.apply")} <ArrowDown size={18} className="group-hover:translate-y-0.5 transition-transform" />
              </button>
              <a
                href="#shein"
                data-testid="hero-shein-btn"
                className="border border-white/25 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 hover:border-white/40 transition-all"
              >
                {t("hero.viewShein")}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.35 }}
              className="flex items-center gap-3 mt-7"
            >
              <div className="flex -space-x-2">
                {["🧑🏽","👷🏽‍♀️","🧔🏽","👩🏽"].map((e, i) => (
                  <span key={i} className="w-8 h-8 rounded-full bg-[#1C2541] border border-white/20 flex items-center justify-center text-sm">{e}</span>
                ))}
              </div>
              <div>
                <div className="flex gap-0.5 text-[#FFD166]">
                  {[...Array(5)].map((_, i) => <Star key={i} size={13} fill="currentColor" />)}
                </div>
                <div className="text-xs text-slate-300 mt-0.5">{t("hero.trust")}</div>
              </div>
            </motion.div>

            <div className="flex flex-wrap gap-x-6 gap-y-3 mt-9">
              <div className="flex items-center gap-2 text-sm text-slate-200"><Home size={18} className="text-[#FFD166]" /> {t("hero.b1")}</div>
              <div className="flex items-center gap-2 text-sm text-slate-200"><Plane size={18} className="text-[#FFD166]" /> {t("hero.b2")}</div>
              <div className="flex items-center gap-2 text-sm text-slate-200"><BadgeCheck size={18} className="text-[#FFD166]" /> {t("hero.b3")}</div>
              <div className="flex items-center gap-2 text-sm text-slate-200"><Sparkles size={18} className="text-[#FFD166]" /> {t("hero.b4")}</div>
              <div className="flex items-center gap-2 text-sm text-slate-200"><Languages size={18} className="text-[#FFD166]" /> {t("hero.b5")}</div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="relative bg-[#1C2541]/90 backdrop-blur-sm rounded-3xl border border-white/10 shadow-2xl overflow-hidden">
              <div className="relative">
                <img
                  src="https://images.pexels.com/photos/31112238/pexels-photo-31112238.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Trabajadores organizando paquetes SHEIN"
                  className="w-full h-60 object-cover"
                />
                <span className="absolute top-4 left-4 bg-white text-[#0B132B] text-xs font-extrabold px-3 py-1.5 rounded-full tracking-wide">SHEIN · Wrocław 🇵🇱</span>
                <div className="absolute inset-0 bg-gradient-to-t from-[#1C2541] via-transparent to-transparent" />
              </div>
              <div className="p-7">
                <div className="text-xs text-slate-400 uppercase tracking-wider">{t("hero.earn")}</div>
                <div className="text-4xl font-head font-extrabold text-[#22C55E] mt-1">$23,530 MXN</div>
                <div className="text-sm text-slate-300">{t("hero.per")}</div>
                <div className="mt-3 flex items-start gap-2 bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-xl px-3 py-2">
                  <TrendingUp size={16} className="text-[#22C55E] mt-0.5 shrink-0" />
                  <span className="text-xs text-slate-200 leading-snug">{t("hero.earnNote")}</span>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-6 pt-6 border-t border-white/10 text-center">
                  {stat("18-55", t("hero.s1"))}
                  {stat("+20", t("hero.s2"))}
                  {stat("100%", t("hero.s3"))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
